export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
export const SPA_URL = import.meta.env.VITE_SPA_URL || 'http://localhost:5167';

export const API_ENDPOINTS = {
  AUTH: {
    CURRENT_USER: '/admin/me',
    LOGOUT: '/shopify/auth/logout',
    LOGIN: '/shopify/auth/login',
    REFRESH: '/shopify/auth/refresh',
  },
  ROLES: {
    LIST: '/admin/roles',
    CREATE: '/admin/roles',
    NEW: '/admin/roles/new',
    SHOW: (id: string) => `/admin/roles/${id}`,
    UPDATE: (id: string) => `/admin/roles/${id}`,
    DELETE: (id: string) => `/admin/roles/${id}`,
    OPTIONS: '/admin/roles/options',
    PERMISSIONS: '/admin/roles/permissions',
  },
  USERS: {
    LIST: '/admin/users',
    CREATE: '/admin/users',
    NEW: '/admin/users/new',
    SHOW: (id: string) => `/admin/users/${id}`,
    UPDATE: (id: string) => `/admin/users/${id}`,
    DELETE: (id: string) => `/admin/users/${id}`,
  },
  DS_AGENTS: {
    LIST: '/admin/ds-agents',
    CREATE: '/admin/ds-agents',
    NEW: '/admin/ds-agents/new',
    SHOW: (id: string) => `/admin/ds-agents/${id}`,
    UPDATE: (id: string) => `/admin/ds-agents/${id}`,
    DELETE: (id: string) => `/admin/ds-agents/${id}`,
  },
  BANNERS: {
    LIST: '/admin/banners',
    CREATE: '/admin/banners',
    NEW: '/admin/banners/new',
    SHOW: (id: string) => `/admin/banners/${id}`,
    UPDATE: (id: string) => `/admin/banners/${id}`,
    DELETE: (id: string) => `/admin/banners/${id}`,
    LOCATIONS: '/admin/banners/locations',
  },
  DISCOUNTS: {
    LIST: '/admin/discounts',
    CREATE: '/admin/discounts',
    NEW: '/admin/discounts/new',
    SHOW: (id: string) => `/admin/discounts/${id}`,
    UPDATE: (id: string) => `/admin/discounts/${id}`,
    DELETE: (id: string) => `/admin/discounts/${id}`,
  },
  DEEP_LINKS: {
    LIST: '/admin/deep-links',
    OPTIONS: '/admin/deep-links/options',
    CREATE: '/admin/deep-links',
    NEW: '/admin/deep-links/new',
    SHOW: (id: string) => `/admin/deep-links/${id}`,
    UPDATE: (id: string) => `/admin/deep-links/${id}`,
    DELETE: (id: string) => `/admin/deep-links/${id}`,
  },
  CUSTOMERS: {
    LIST: '/admin/customers',
    SHOW: (id: string) => `/admin/customers/${id}`,
    SUBSCRIPTION_CANCEL: (id: number) => `/admin/customers/${id}/subscription-plans/cancel`,
    SUBSCRIPTION_PAUSE: (id: number) => `/admin/customers/${id}/subscription-plans/pause`,
    SUBSCRIPTION_REACTIVATE: (id: number) => `/admin/customers/${id}/subscription-plans/reactivate`,
    SUBSCRIPTION_LOCK: (id: number) => `/admin/customers/${id}/subscription-plans/lock`,
    SUBSCRIPTION_UNLOCK: (id: number) => `/admin/customers/${id}/subscription-plans/unlock`,
    ORDER_SHIP_NOW: (id: number) => `/admin/customers/${id}/orders/next/ship-now`,
    ORDER_CHANGE_DATE: (id: number) => `/admin/customers/${id}/orders/next/dispatch-date`,
    CANCELLATION_REASONS: '/admin/cancellation-reasons',
    LAST_ORDER_DATE: (id: number) => `/admin/customers/${id}/orders/last-order-date`,
    SUBSCRIPTION_CONTRACT: (id: string) => `/admin/customers/${id}/subscription-contract`,
    SHIPPING_OPTION: (id: string) => `/admin/customers/${id}/shipping-option`,
  },
  PAYMENT_RETRIES: {
    LIST: '/admin/payment-retries',
    UPDATE: (id: string) => `/admin/payment-retries/${id}/cancel`,
  },
  AUDIT_LOGS: {
    LIST: '/admin/audit-logs',
  },
  REPLACEMENT_BATCHES: {
    LIST: '/admin/replacement-batches',
    CREATE: '/admin/replacement-batches',
    NEW: '/admin/replacement-batches/new',
    SHOW: (id: string) => `/admin/replacement-batches/${id}`,
    UPDATE: (id: string) => `/admin/replacement-batches/${id}`,
    DELETE: (id: string) => `/admin/replacement-batches/${id}`,
    PRODUCTS: '/admin/replacement-batches/products',
  },
  REMOVAL_BATCHES: {
    LIST: '/admin/removal-batches',
    CREATE: '/admin/removal-batches',
    NEW: '/admin/removal-batches/new',
    SHOW: (id: string) => `/admin/removal-batches/${id}`,
    UPDATE: (id: string) => `/admin/removal-batches/${id}`,
    DELETE: (id: string) => `/admin/removal-batches/${id}`,
    PRODUCTS: '/admin/removal-batches/products',
  },
  BLOCKED_MTO_REQUESTS: {
    LIST: '/admin/blocked-mto-requests',
  },
  CHECKOUT_VALIDATION_ERRORS: {
    LIST: '/admin/checkout-validation-errors',
  },
  TRIAL_FUNNEL_ERRORS: {
    LIST: '/admin/trial-funnel-errors',
  },
  PRODUCTS: {
    LIST: '/admin/products',
    CREATE: '/admin/products',
    NEW: '/admin/products/new',
    SHOW: (id: string) => `/admin/products/${id}`,
    UPDATE: (id: string) => `/admin/products/${id}`,
    BASIC_DETAILS: (id: string) => `/admin/products/${id}/basic-details`,
    SUBSCRIPTION_CONFIGURATION: (id: string) => `/admin/products/${id}/subscription-configuration`,
    PROMOTIONS_BADGES: (id: string) => `/admin/products/${id}/promotions-badges`,
    ADDITIONAL_INFORMATION: (id: string) => `/admin/products/${id}/additional-information`,
    PREFILL: '/admin/products/prefill',
    REFRESH: '/admin/products/refresh',
    AVAILABLE_SWAP_PRODUCTS: '/admin/products/available-swap-products',
    FILTER_OPTIONS: '/admin/products/filter-options',
    IMAGES: (id: string) => `/admin/products/${id}/images`,
    IMAGES_SYNC: (id: string) => `/admin/products/${id}/images/sync`,
    IMAGE: (id: string, imageId: string | number) => `/admin/products/${id}/images/${imageId}`,
    IMAGES_BULK_DELETE: (id: string) => `/admin/products/${id}/images/bulk-delete`,
    BADGES: (productId: string) => `/admin/products/${productId}/badges`,
    VARIETY_PACK_CANDIDATES: (id: string) => `/admin/products/${id}/variety-pack-candidates`,
    VARIETY_PACK: (id: string) => `/admin/products/${id}/variety-pack`,
    ORDERING: '/admin/products/ordering',
  },
  BADGES: {
    OPTIONS: '/admin/badges/options',
  },
  TRIAL_WEBHOOKS: {
    REPROCESS: (id: string) => `/admin/trial-webhooks/${encodeURIComponent(id)}/reprocess`,
  },
  UPSELL_CONFIG: {
    PRODUCTS: '/admin/upsell-products',
    SETTINGS: '/admin/settings/upsell-settings',
  },
  RAF_SETTINGS: '/admin/raf-settings',
  BROKEN_SUBSCRIPTIONS: {
    LIST: '/admin/broken-subscriptions',
    PROBLEM_CODES: '/admin/broken-subscriptions/problem-codes',
    SHOW: (shopifyCustomerId: number) => `/admin/broken-subscriptions/${shopifyCustomerId}`,
    REVALIDATE: (shopifyCustomerId: number) =>
      `/admin/broken-subscriptions/${shopifyCustomerId}/revalidate`,
    SHOPIFY_LINE_UPDATE: (shopifyCustomerId: number, shopifyLineId: string) =>
      `/admin/broken-subscriptions/${shopifyCustomerId}/shopify-line/${shopifyLineId}`,
    SHOPIFY_LINE_DELETE: (shopifyCustomerId: number, shopifyLineId: string) =>
      `/admin/broken-subscriptions/${shopifyCustomerId}/shopify-line/${shopifyLineId}`,
    SHOPIFY_DISCOUNT_DELETE: (shopifyCustomerId: number, shopifyDiscountId: string) =>
      `/admin/broken-subscriptions/${shopifyCustomerId}/shopify-discount/${shopifyDiscountId}`,
    PRODUCT_UPDATE: (shopifyCustomerId: number, productId: number) =>
      `/admin/broken-subscriptions/${shopifyCustomerId}/product/${productId}`,
    PRODUCT_DELETE: (shopifyCustomerId: number, productId: number) =>
      `/admin/broken-subscriptions/${shopifyCustomerId}/product/${productId}`,
    DISCOUNT_DELETE: (shopifyCustomerId: number, discountId: number) =>
      `/admin/broken-subscriptions/${shopifyCustomerId}/discount/${discountId}`,
  },
  PRODUCT_GROUPINGS: {
    CREATE: '/admin/product-groupings',
    DELETE: (productId: string) => `/admin/product-groupings/${productId}`,
    AVAILABLE: (productId: string) => `/admin/product-groupings/available/${productId}`,
  },
} as const;

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;
